import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { NativeCollabQuery } from '@confluence/native-collab/entry-points/NativeCollabQuery.graphql.ts';
import type {
	NativeCollabQueryResponse,
	NativeCollabQueryVariables,
} from '@confluence/native-collab/entry-points/NativeCollabQuery.ts';
import { CollabDraftQuery } from '@confluence/fabric-providers/entry-points/CollabDraftQuery.ts';
import type {
	CollabDraftQueryRes,
	CollabDraftQueryVariables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery.ts';
import { isDraftSSRPreloadPermissionError } from '@confluence/fabric-providers/entry-points/preloadDraft.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';

import { findSmartCardNodes } from './utils/findSmartCardNodes.ts';

export function preloadCollabDraftContent(
	contentId: string,
	draftShareId: string | undefined,
	useCache: boolean,
) {
	return query<CollabDraftQueryRes, CollabDraftQueryVariables>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: CollabDraftQuery,
		variables: {
			id: contentId,
			hydrateAdf: true,
			...(draftShareId ? { draftShareId } : undefined),
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
	}).then(async (result: any) => {
		// There is a bug in appolo client (https://github.com/apollographql/react-apollo/issues/3918)
		// that doesn't preserve the result in caches if there is an error in SSR
		// So we need to preserve the error, to be able to verify it later during render phase in SSR
		if (result?.errors?.some((err) => isDraftSSRPreloadPermissionError(err?.message))) {
			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('edit');
		}

		// There are two reasons to store smart card URLs on the window object:
		// 1. The OTP uses a document snapshot from Confluence that may be outdated and miss recently added or removed smart cards,
		//    so we must reconcile the list of smart cards with the draft content.
		// 2. The draft content stores links with slugs, but the Confluence snapshot does not,
		//    so we need to add slugs sourced from the draft content.
		// Therefore, we extract smart cards from the draft content here, save them on the window object,
		// and later use them to align the Confluence snapshot with the draft content.
		window['__SMART_CARDS_IN_DRAFT__'] = findSmartCardNodes(
			result?.data?.collabDraft?.document || '',
		);

		return result;
	});
}

export function preloadNativeCollab(
	contentId: string,
	draftShareId: string | undefined,
	useCache: boolean,
	status?: [string],
) {
	return query<NativeCollabQueryResponse, NativeCollabQueryVariables>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: NativeCollabQuery,
		variables: {
			contentId,
			...(draftShareId && { draftShareId }),
			status: status ?? ['draft', 'current'],
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
		context: { single: 'true' },
	});
}
